// Custom TipTap extension that adds a numeric `indent` attribute to block
// nodes (paragraph + heading by default) and exposes `indent()` / `outdent()`
// commands the toolbar can call. Levels are 0..maxLevel; each level renders
// as `padding-left: indent * stepRem` on the node so the HTML round-trips
// via dangerouslySetInnerHTML — no separate renderer logic needed.
//
// StarterKit already handles list nesting (Tab inside a list item). This
// extension is intentionally for non-list blocks only. Inside a list, the
// node is a `listItem` (or text inside a list-item's paragraph that has a
// list ancestor); we still allow paragraph to carry an indent attribute,
// but the toolbar button is wired so that calling `indent()` on a cursor
// inside a list is a no-op for the user — list nesting stays Tab-driven.

import { Extension } from "@tiptap/core";

export type IndentOptions = {
  /** Node types that can carry an indent attribute. */
  types: string[];
  minLevel: number;
  maxLevel: number;
  /** Rem multiplier per level. 2rem matches the toolbar's visual step. */
  stepRem: number;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

export const Indent = Extension.create<IndentOptions>({
  name: "indent",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      minLevel: 0,
      maxLevel: 4,
      stepRem: 2,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const attr = element.getAttribute("data-indent");
              if (attr != null) {
                const n = parseInt(attr, 10);
                return Number.isFinite(n) ? n : 0;
              }
              // Fall back to parsing inline padding-left (handles content
              // that was authored before this extension shipped but with
              // a padding-left style applied another way).
              const style = element.getAttribute("style") ?? "";
              const match = style.match(/padding-left:\s*([\d.]+)rem/i);
              if (match) {
                const level = Math.round(parseFloat(match[1]) / this.options.stepRem);
                return Math.max(0, Math.min(this.options.maxLevel, level));
              }
              return 0;
            },
            renderHTML: (attrs) => {
              const level = Number(attrs.indent) || 0;
              if (level <= 0) return {};
              return {
                "data-indent": String(level),
                style: `padding-left: ${level * this.options.stepRem}rem`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ state, dispatch }) => {
          const { selection, tr } = state;
          const { from, to } = selection;
          let modified = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (!this.options.types.includes(node.type.name)) return;
            const current = Number(node.attrs.indent) || 0;
            const next = Math.min(current + 1, this.options.maxLevel);
            if (next !== current) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
              modified = true;
            }
          });

          if (modified && dispatch) dispatch(tr);
          return modified;
        },

      outdent:
        () =>
        ({ state, dispatch }) => {
          const { selection, tr } = state;
          const { from, to } = selection;
          let modified = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (!this.options.types.includes(node.type.name)) return;
            const current = Number(node.attrs.indent) || 0;
            const next = Math.max(current - 1, this.options.minLevel);
            if (next !== current) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
              modified = true;
            }
          });

          if (modified && dispatch) dispatch(tr);
          return modified;
        },
    };
  },
});

export default Indent;
